import { useLocalization } from '@lib/localization';
import { Box, Paper } from '@mui/material';
import { type LineSeriesType } from '@mui/x-charts';
import { LineChart } from '@mui/x-charts/LineChart';
import useNoticeColors from '@store/hooks/useNoticeColors';
import { useMemo } from 'react';

export interface TrendDataPoint {
  date: Date;
  total: number;
  ok: number;
  notChecked: number;
  warning: number;
  error: number;
}

interface InspectionTrendChartProps {
  data: TrendDataPoint[];
  order?: 'asc' | 'desc';
}

const InspectionTrendChart: React.FC<InspectionTrendChartProps> = ({ data, order = 'asc' }) => {
  const { t, currentLanguage } = useLocalization();
  const noticeColors = useNoticeColors();

  const labelColor = 'rgba(255,255,255,0.7)';

  const dataset = useMemo(() => {
    return [...data]
      .map((d) => ({ ...d, date: new Date(d.date) }))
      .sort((a, b) =>
        order === 'asc' ? a.date.getTime() - b.date.getTime() : b.date.getTime() - a.date.getTime(),
      );
  }, [data, order]);

  return (
    <Paper
      sx={{
        p: 3,
        pl: 0,
        height: 400,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: 1 }}>
        <LineChart
          height={360}
          dataset={dataset}
          yAxis={[
            {
              tickMinStep: 1
            },
          ]}
          xAxis={[
            {
              dataKey: 'date',
              scaleType: 'time',
              valueFormatter: (value, context) => {
                const date = new Date(value);
                if (context.location === 'tick') {
                  return date.toLocaleDateString(currentLanguage, {
                    day: '2-digit',
                    month: 'short',
                  });
                }
                return date.toLocaleDateString(currentLanguage, {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                });
              },
            },
          ]}
          series={
            [
              {
                dataKey: 'ok',
                label: t('Common_Successful'),
                color: noticeColors.Ok,
                showMark: true,
                curve: 'monotoneX',
              },
              {
                dataKey: 'warning',
                label: t('Common_Warning'),
                color: noticeColors.Warning,
                showMark: true,
                curve: 'monotoneX',
              },
              {
                dataKey: 'error',
                label: t('Common_Error'),
                color: noticeColors.Error,
                showMark: true,
                curve: 'monotoneX',
              },
              {
                dataKey: 'notChecked',
                label: t('Common_NotChecked'),
                color: noticeColors.NotChecked,
                showMark: true,
                curve: 'monotoneX',
              },
            ] as LineSeriesType[]
          }
          grid={{ horizontal: true, vertical: false }}
          margin={{ left: 0, right: 20, top: 20, bottom: 40 }}
          slotProps={{
            legend: {
              direction: 'horizontal',
              position: { vertical: 'top', horizontal: 'end' },
            },
            tooltip: {
              sx: {
                '*': {
                  color: '#fff !important',
                },
              },
            },
            noDataOverlay: { message: t('Project_Overview_NoData') },
          }}
          sx={{
            '--Charts-axis-label-color': labelColor,
            '& .MuiLineElement-root': { strokeWidth: 2 },
            '& .MuiMarkElement-root': { r: 3 },
          }}
        />
      </Box>
    </Paper>
  );
};

export default InspectionTrendChart;
